/* ==========================================================================
   Shipzik — theme.js
   No framework, no build step. Progressive enhancement over server-rendered
   Liquid: every page works without this file, it just gets nicer with it.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  const $  = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const usd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  /** cents -> "$12.34" */
  const money = (cents) => usd.format((Number(cents) || 0) / 100);

  /**
   * Per-unit price. Keeps the extra decimals on purpose — $0.527 a box is not
   * $0.53 when you buy 640 — with a fixed digit count so columns stay aligned.
   */
  function unitMoney(cents) {
    const v = (Number(cents) || 0) / 100;
    if (v >= 1) return usd.format(v);
    return '$' + v.toFixed(v >= 0.1 ? 3 : 4);
  }

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  const getJSON = (url) => fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /** Find the tier a quantity falls into. */
  function tierFor(tiers, qty) {
    if (!Array.isArray(tiers) || !tiers.length) return null;
    let match = tiers[0];
    for (const t of tiers) {
      if (qty >= t.min && (t.max == null || qty <= t.max)) match = t;
    }
    return match;
  }

  function nextTier(tiers, qty) {
    if (!Array.isArray(tiers)) return null;
    return tiers.find((t) => t.min > qty) || null;
  }

  /* ------------------------------------------------------- quantity input */

  function qtyValue(scope) {
    const input = $('[data-qty-input]', scope);
    return input ? Math.max(parseInt(input.min || '1', 10), parseInt(input.value, 10) || 1) : 1;
  }

  function setQty(scope, value) {
    const input = $('[data-qty-input]', scope);
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, Math.round(value));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', (e) => {
    const up = e.target.closest('[data-qty-up]');
    const down = e.target.closest('[data-qty-down]');
    if (!up && !down) return;
    const scope = e.target.closest('[data-qty]');
    setQty(scope, qtyValue(scope) + (up ? 1 : -1));
  });

  /* -------------------------------------------------------- product page  */

  function initProductPage() {
    const root = $('[data-product-root]');
    if (!root) return;

    let tiers = [];
    try { tiers = JSON.parse(root.dataset.tiers || '[]'); } catch (_) {}

    const unitsPer   = parseInt(root.dataset.unitsPer || '1', 10);
    const baseUnit   = root.dataset.baseUnit || 'unit';
    const basePlural = root.dataset.baseUnitPlural || 'units';
    const sellUnit   = root.dataset.sellUnit || 'each';
    const sellPlural = root.dataset.sellUnitPlural || 'each';

    const qtyScope   = $('.pdp__buy [data-qty]', root);
    const priceEl    = $('[data-pdp-price]', root);
    const unitEl     = $('[data-pdp-unit-price]', root);
    const totalEl    = $('[data-pdp-total]', root);
    const savingsEl  = $('[data-pdp-savings]', root);
    const summaryEl  = $('[data-pdp-qty-summary]', root);
    const barPrice   = $('[data-buy-bar-price]');
    const nudge      = $('[data-tier-nudge]', root);
    const nudgeText  = $('[data-tier-nudge-text]', root);
    const nudgeApply = $('[data-tier-nudge-apply]', root);

    if (!qtyScope || !tiers.length) return;

    function render() {
      const qty = qtyValue(qtyScope);
      const tier = tierFor(tiers, qty) || tiers[0];
      const base = tiers[0];

      if (priceEl) priceEl.textContent = money(tier.price);
      if (barPrice) barPrice.textContent = money(tier.price);
      if (unitEl && unitsPer > 1) unitEl.textContent = `${unitMoney(tier.price / unitsPer)} / ${baseUnit}`;

      const total = tier.price * qty;
      if (totalEl) totalEl.textContent = money(total);

      const saved = (base.price - tier.price) * qty;
      if (savingsEl) {
        if (saved > 0) {
          savingsEl.textContent = `— you save ${money(saved)}`;
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }

      if (summaryEl) {
        summaryEl.textContent =
          unitsPer > 1
            ? `You're buying ${(qty * unitsPer).toLocaleString()} ${basePlural}`
            : `${qty} ${qty === 1 ? sellUnit : sellPlural} in the cart`;
      }

      // Highlight the live tier row
      $$('[data-tier-row]', root).forEach((row) => {
        row.classList.toggle('is-active', parseInt(row.dataset.min, 10) === tier.min);
      });

      // Nudge toward the next break, but only when it's genuinely close.
      const next = nextTier(tiers, qty);
      if (nudge && next) {
        const gap = next.min - qty;
        const worthIt = gap <= Math.max(3, Math.ceil(next.min * 0.25));
        if (worthIt) {
          const saveAtNext = (tier.price - next.price) * next.min;
          nudgeText.innerHTML =
            `Add <b>${gap} more ${gap === 1 ? sellUnit : sellPlural}</b> to reach ` +
            `<b>${money(next.price)}</b> each — ${money(saveAtNext)} off the ${next.min}-${sellUnit} total.`;
          nudge.hidden = false;
          nudge.dataset.target = next.min;
        } else {
          nudge.hidden = true;
        }
      } else if (nudge) {
        nudge.hidden = true;
      }
    }

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-qty-input]')) render();
    });
    root.addEventListener('input', (e) => {
      if (e.target.matches('[data-qty-input]')) render();
    });

    // Jump to a tier's quantity. The <button> in the first cell carries the
    // semantics; the whole row is clickable as a mouse convenience on top.
    $$('[data-tier-row]', root).forEach((row) => {
      row.addEventListener('click', () => {
        setQty(qtyScope, parseInt(row.dataset.min, 10));
        render();
      });
    });

    if (nudgeApply) {
      nudgeApply.addEventListener('click', () => {
        setQty(qtyScope, parseInt(nudge.dataset.target, 10));
        render();
      });
    }

    // Sticky mobile buy bar appears once the real button scrolls away.
    const addBtn = $('[data-pdp-add]', root);
    const bar = $('[data-buy-bar]');
    if (addBtn && bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
        { rootMargin: '0px 0px -80px 0px' }
      ).observe(addBtn);
      $('[data-buy-bar-add]').addEventListener('click', () => addBtn.click());
    }

    render();
  }

  /* ------------------------------------------------------------ hero card */
  // The hero embeds a live tier table; make its rows feel alive too.
  function initHeroTiers() {
    const hero = $('.hero__card');
    if (!hero || $('[data-product-root]')) return;
    $$('[data-tier-row]', hero).forEach((row) => {
      row.addEventListener('mouseenter', () => {
        $$('[data-tier-row]', hero).forEach((r) => r.classList.remove('is-active'));
        row.classList.add('is-active');
      });
    });
  }

  /* ----------------------------------------------------------------- cart */

  const Cart = {
    async add(id, quantity) {
      return postJSON('/cart/add.js', { items: [{ id, quantity }] });
    },
    async get() {
      return getJSON('/cart.js');
    },
    async change(line, quantity) {
      return postJSON('/cart/change.js', { line, quantity });
    },
  };

  const drawer = $('[data-cart-drawer]');

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('.drawer__close', drawer)?.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function fallbackThumb() {
    return `<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor"
      stroke-width="1.5" stroke-linejoin="round"><path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2z"/>
      <path d="M4 7.2l8 4.3 8-4.3"/><path d="M12 11.5V21"/></svg>`;
  }

  function renderDrawer(cart) {
    if (!drawer) return;

    const body = $('[data-drawer-body]', drawer);
    const foot = $('[data-drawer-foot]', drawer);
    const count = $('[data-drawer-count]', drawer);

    if (count) count.textContent = cart.item_count ? `(${cart.item_count})` : '';

    if (!cart.item_count) {
      body.innerHTML =
        `<div class="empty-state"><p class="t-muted">Your cart is empty.</p>
         <a class="btn btn--ghost" href="/collections">Browse categories</a></div>`;
      foot.hidden = true;
      return;
    }

    body.innerHTML = cart.items
      .map((item, i) => {
        const saved = (item.original_line_price || item.line_price) - item.line_price;
        return `
        <div class="drawer-line">
          <div class="drawer-line__media">
            ${item.image ? `<img src="${item.image}" alt="" width="64" height="64">` : fallbackThumb()}
          </div>
          <div>
            <a class="drawer-line__title" href="${item.url}">${item.product_title}</a>
            <div class="t-xs t-muted">${item.sku || ''}</div>
            ${saved > 0 ? `<div class="cart-line__tier">Volume tier — saving ${money(saved)}</div>` : ''}
            <div class="drawer-line__foot">
              <div class="qty qty--sm" data-qty>
                <button type="button" class="qty__btn" data-drawer-dec="${i + 1}" aria-label="Decrease">–</button>
                <input class="qty__input" type="number" value="${item.quantity}" min="0"
                       data-drawer-qty="${i + 1}" aria-label="Quantity">
                <button type="button" class="qty__btn" data-drawer-inc="${i + 1}" aria-label="Increase">+</button>
              </div>
              <strong class="t-sm">${money(item.line_price)}</strong>
            </div>
          </div>
        </div>`;
      })
      .join('');

    foot.hidden = false;

    const savings = (cart.original_total_price || cart.total_price) - cart.total_price;
    const savingsRow = $('[data-drawer-savings]', drawer);
    if (savingsRow) {
      savingsRow.hidden = savings <= 0;
      $('[data-drawer-savings-value]', drawer).textContent = '−' + money(savings);
    }
    $('[data-drawer-subtotal]', drawer).textContent = money(cart.total_price);
  }

  function updateCartCount(cart) {
    const el = $('[data-cart-count]');
    if (!el) return;
    el.textContent = cart.item_count;
    el.hidden = cart.item_count === 0;
    el.classList.remove('is-bumped');
    void el.offsetWidth;
    el.classList.add('is-bumped');
  }

  async function refreshCart(open) {
    const cart = await Cart.get();
    updateCartCount(cart);
    renderDrawer(cart);
    if (open) openDrawer();
    return cart;
  }

  function initCart() {
    document.addEventListener('click', async (e) => {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        refreshCart(true);
        return;
      }
      if (e.target.closest('[data-drawer-close]')) {
        closeDrawer();
        return;
      }

      // Card "Add" buttons
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        const card = addBtn.closest('[data-product-card]');
        const qty = card ? qtyValue($('[data-qty]', card)) : 1;
        const label = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        try {
          await Cart.add(Number(addBtn.dataset.variantId), qty);
          await refreshCart(true);
        } catch (err) {
          console.error(err);
          alert('Could not add that to the cart.');
        } finally {
          addBtn.disabled = false;
          addBtn.textContent = label;
        }
        return;
      }

      // Cart page: remove a line
      const rm = e.target.closest('[data-cart-remove]');
      if (rm) {
        e.preventDefault();
        await Cart.change(Number(rm.dataset.cartRemove), 0);
        window.location.reload();
        return;
      }

      // Drawer quantity buttons
      const inc = e.target.closest('[data-drawer-inc]');
      const dec = e.target.closest('[data-drawer-dec]');
      if (inc || dec) {
        const line = Number((inc || dec).dataset.drawerInc || (dec).dataset.drawerDec);
        const input = $(`[data-drawer-qty="${line}"]`, drawer);
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + (inc ? 1 : -1));
        renderDrawer(await Cart.change(line, next));
        updateCartCount(await Cart.get());
      }
    });

    document.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-drawer-qty]');
      if (!input) return;
      const line = Number(input.dataset.drawerQty);
      const cart = await Cart.change(line, Math.max(0, parseInt(input.value, 10) || 0));
      renderDrawer(cart);
      updateCartCount(cart);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closeDrawer(); closeMobileNav(); }
    });

    // PDP form: intercept so we can open the drawer instead of navigating.
    const form = $('#ProductForm') || $('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = Number(form.querySelector('[name="id"]').value);
        const qty = qtyValue(form.querySelector('[data-qty]')) || 1;
        const btn = $('[data-pdp-add]', form);
        const label = btn ? btn.innerHTML : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
        try {
          await Cart.add(id, qty);
          await refreshCart(true);
        } catch (err) {
          console.error(err);
        } finally {
          if (btn) { btn.disabled = false; btn.innerHTML = label; }
        }
      });
    }
  }

  /* ------------------------------------------------------ mobile nav ---- */

  function closeMobileNav() {
    $('[data-mobile-nav]')?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function initMobileNav() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-nav-open]')) {
        $('[data-mobile-nav]')?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
      if (e.target.closest('[data-mobile-nav-close]')) closeMobileNav();
    });
  }

  /* ---------------------------------------------------- predictive search */

  function initSearch() {
    $$('[data-predictive-search]').forEach((form) => {
      const input = $('[data-search-input]', form);
      const results = $('[data-search-results]', form);
      if (!input || !results) return;

      const run = debounce(async () => {
        const q = input.value.trim();
        if (q.length < 2) { results.hidden = true; return; }
        try {
          const data = await getJSON(
            `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`
          );
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<div class="search__empty">Nothing matches “${q}”. We carry about 60 SKUs — <a href="/pages/contact" style="text-decoration:underline">ask us to add it</a>.</div>`;
          } else {
            results.innerHTML = products
              .map(
                (p) => `
                <a class="search__result" href="${p.url}">
                  ${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="">` : `<span>${fallbackThumb()}</span>`}
                  <span>
                    <span class="search__result-title">${p.title}</span>
                    <span class="search__result-meta">${p.sku || ''}${p.unit_hint ? ' · ' + p.unit_hint : ''}</span>
                  </span>
                  <span class="search__result-price">${money(p.price)}</span>
                </a>`
              )
              .join('');
          }
          results.hidden = false;
        } catch (_) {
          results.hidden = true;
        }
      }, 160);

      input.addEventListener('input', run);
      input.addEventListener('focus', run);
      document.addEventListener('click', (e) => {
        if (!form.contains(e.target)) results.hidden = true;
      });
    });
  }

  /* ------------------------------------------------------------ PLP tools */

  function initPLP() {
    const plp = $('[data-plp]');
    if (!plp) return;

    const grid = $('[data-plp-grid]', plp);
    const cards = $$('[data-product-card]', grid);
    const countEl = $('[data-plp-count]');
    const empty = $('[data-plp-empty]');
    const facets = $('[data-facets]', plp);

    function activeFilters() {
      const groups = {};
      $$('input[data-facet]:checked', facets).forEach((input) => {
        (groups[input.dataset.facet] ||= []).push(input.value);
      });
      return groups;
    }

    function matches(card, groups) {
      return Object.entries(groups).every(([key, values]) => {
        if (key === 'price') {
          const price = Number(card.dataset.price);
          return values.some((range) => {
            const [lo, hi] = range.split('-').map(Number);
            return price >= lo && price <= hi;
          });
        }
        return values.includes(card.dataset[key]);
      });
    }

    function apply() {
      const groups = activeFilters();
      let visible = 0;
      cards.forEach((card) => {
        const show = matches(card, groups);
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      if (countEl) countEl.textContent = visible;
      if (empty) empty.classList.toggle('hide', visible > 0);
      grid.classList.toggle('hide', visible === 0);
    }

    function sort(mode) {
      const sorted = cards.slice().sort((a, b) => {
        switch (mode) {
          case 'price-asc':  return Number(a.dataset.price) - Number(b.dataset.price);
          case 'price-desc': return Number(b.dataset.price) - Number(a.dataset.price);
          case 'title-asc':  return a.dataset.title.localeCompare(b.dataset.title);
          default:           return cards.indexOf(a) - cards.indexOf(b);
        }
      });
      sorted.forEach((card) => grid.appendChild(card));
    }

    facets?.addEventListener('change', apply);
    $('[data-plp-sort]')?.addEventListener('change', (e) => sort(e.target.value));
    $$('[data-facets-clear]').forEach((btn) =>
      btn.addEventListener('click', () => {
        $$('input[data-facet]', facets).forEach((i) => (i.checked = false));
        apply();
      })
    );
    $('[data-facets-open]')?.addEventListener('click', () => {
      facets.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });
    $('[data-facets-close]')?.addEventListener('click', () => {
      facets.classList.remove('is-open');
      document.body.style.overflow = '';
    });
  }

  /* --------------------------------------------------------- quick order */

  function initQuickOrder() {
    const root = $('[data-quick-order]');
    if (!root) return;

    let catalog = [];
    try { catalog = JSON.parse($('[data-qo-catalog]')?.textContent || '[]'); } catch (_) {}
    const bySku = new Map(catalog.map((p) => [String(p.sku).toUpperCase(), p]));

    const rows = $('[data-qo-rows]', root);
    const totalEl = $('[data-qo-total]', root);

    function addRow(sku = '', qty = '') {
      const el = document.createElement('div');
      el.className = 'qo-row';
      el.innerHTML = `
        <input class="input" type="text" placeholder="SKU — e.g. SZ-BOX-1296" value="${sku}" data-qo-sku
               list="QoSkuList" autocomplete="off">
        <input class="input" type="number" min="1" step="1" placeholder="Qty" value="${qty}" data-qo-qty>
        <button type="button" class="qo-remove" data-qo-remove aria-label="Remove line">×</button>
        <div class="qo-row__status" data-qo-status></div>`;
      rows.appendChild(el);
      return el;
    }

    function priceLine(row) {
      const sku = $('[data-qo-sku]', row).value.trim().toUpperCase();
      const qty = parseInt($('[data-qo-qty]', row).value, 10) || 0;
      const status = $('[data-qo-status]', row);
      const product = bySku.get(sku);

      if (!sku) { status.textContent = ''; status.className = 'qo-row__status'; return 0; }
      if (!product) {
        status.textContent = `No SKU “${sku}” in the catalog.`;
        status.className = 'qo-row__status is-bad';
        return 0;
      }
      if (qty < 1) {
        status.textContent = `${product.title} — enter a quantity`;
        status.className = 'qo-row__status';
        return 0;
      }

      const tier = tierFor(product.tiers, qty) || { price: product.price };
      const base = (product.tiers && product.tiers[0]) || { price: product.price };
      const line = tier.price * qty;
      const saved = (base.price - tier.price) * qty;

      status.innerHTML =
        `${product.title} — ${qty} ${qty === 1 ? product.sell_unit : product.sell_unit_plural} ` +
        `at ${money(tier.price)} = <b>${money(line)}</b>` +
        (saved > 0 ? ` · volume saving ${money(saved)}` : '');
      status.className = 'qo-row__status is-ok';
      row.dataset.variantId = product.id;
      row.dataset.qty = qty;
      return line;
    }

    function repriceAll() {
      const total = $$('.qo-row', rows).reduce((sum, row) => sum + priceLine(row), 0);
      totalEl.textContent = money(total);
    }

    root.addEventListener('input', (e) => {
      if (e.target.matches('[data-qo-sku], [data-qo-qty]')) repriceAll();
    });

    root.addEventListener('click', async (e) => {
      if (e.target.closest('[data-qo-remove]')) {
        e.target.closest('.qo-row').remove();
        if (!$$('.qo-row', rows).length) addRow();
        repriceAll();
        return;
      }
      if (e.target.closest('[data-qo-add-row]')) { addRow(); return; }
      if (e.target.closest('[data-qo-paste-toggle]')) {
        const panel = $('[data-qo-paste]', root);
        panel.hidden = !panel.hidden;
        return;
      }
      if (e.target.closest('[data-qo-paste-parse]')) {
        const text = $('[data-qo-paste-input]', root).value;
        rows.innerHTML = '';
        text.split(/\r?\n/).forEach((raw) => {
          const line = raw.trim();
          if (!line) return;
          // Trailing integer is the quantity; everything before it is the SKU.
          // Comma, tab, semicolon or plain spaces all separate the two.
          const m = line.match(/^(.+?)[\s,;]+(\d+)$/);
          if (m) addRow(m[1].trim(), m[2]);
          else addRow(line, '');
        });
        if (!$$('.qo-row', rows).length) addRow();
        $('[data-qo-paste]', root).hidden = true;
        repriceAll();
        return;
      }
      if (e.target.closest('[data-qo-submit]')) {
        const btn = e.target.closest('[data-qo-submit]');
        const items = $$('.qo-row', rows)
          .filter((r) => r.dataset.variantId && Number(r.dataset.qty) > 0)
          .map((r) => ({ id: Number(r.dataset.variantId), quantity: Number(r.dataset.qty) }));
        if (!items.length) { alert('Add at least one valid SKU and quantity.'); return; }
        btn.disabled = true;
        try {
          await postJSON('/cart/add.js', { items });
          await refreshCart(true);
        } catch (err) {
          console.error(err);
        } finally {
          btn.disabled = false;
        }
      }
    });

    // SKU autocomplete
    const datalist = document.createElement('datalist');
    datalist.id = 'QoSkuList';
    datalist.innerHTML = catalog.map((p) => `<option value="${p.sku}">${p.title}</option>`).join('');
    document.body.appendChild(datalist);

    addRow(); addRow(); addRow();
    repriceAll();
  }

  /* -------------------------------------------------------------- account */

  function initAccount() {
    // Reorder — the whole reason a repeat buyer logs in.
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-reorder]');
      if (!btn) return;
      e.preventDefault();

      let items = [];
      try { items = JSON.parse(btn.dataset.reorder); } catch (_) {}
      items = items.filter((i) => i.id && i.quantity > 0);
      if (!items.length) return;

      const label = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        await postJSON('/cart/add.js', { items });
        await refreshCart(true);
      } catch (err) {
        console.error(err);
        // A discontinued SKU is the normal failure here, so say so.
        alert('Some of those are no longer in the catalog. Add the rest from the order page.');
      } finally {
        btn.disabled = false;
        btn.innerHTML = label;
      }
    });

    // Copy the SKU list for pasting into the quick-order pad
    const copyBtn = $('[data-copy-skus]');
    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const text = $('[data-sku-block]')?.textContent.trim() || '';
        try {
          await navigator.clipboard.writeText(text);
          copyBtn.textContent = 'Copied';
          setTimeout(() => (copyBtn.textContent = 'Copy list'), 1800);
        } catch (_) {
          // Clipboard is blocked outside a secure context — select it instead.
          const range = document.createRange();
          range.selectNodeContents($('[data-sku-block]'));
          const sel = getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      });
    }

    // Address add/edit panels
    $$('[data-address-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.addressToggle;
        const panel = document.getElementById(key === 'new' ? 'AddressNew' : `Address${key}`);
        if (panel) panel.hidden = !panel.hidden;
      });
    });

    // Login ⇄ recover password, both on the login screen
    const login = document.getElementById('login');
    const recover = document.getElementById('recover');
    if (login && recover) {
      const show = (which) => {
        login.style.display = which === 'login' ? '' : 'none';
        recover.style.display = which === 'recover' ? '' : 'none';
      };
      if (location.hash === '#recover' || location.search.includes('recover=true')) show('recover');
      $$('[data-auth-show]').forEach((el) =>
        el.addEventListener('click', (e) => { e.preventDefault(); show(el.dataset.authShow); })
      );
    }
  }

  /* ------------------------------------------------------ cart attributes */

  /**
   * PO number and anything else that travels with the order. The field is a
   * real named input inside the cart form, so it already saves on submit with
   * JS off — this just saves it as you type so nobody loses it by clicking
   * Checkout straight away.
   */
  function initCartAttributes() {
    $$('[data-cart-attribute]').forEach((input) => {
      const key = input.dataset.cartAttribute;
      const status = input.parentElement?.querySelector('[data-cart-attribute-status]');

      const save = debounce(async () => {
        try {
          await postJSON('/cart/update.js', { attributes: { [key]: input.value } });
          if (status) {
            status.textContent = input.value ? 'Saved' : '';
            status.style.color = 'var(--sz-ok)';
          }
        } catch (err) {
          console.error(err);
          if (status) {
            status.textContent = "Couldn't save — it'll be captured at checkout";
            status.style.color = 'var(--sz-ink-3)';
          }
        }
      }, 700);

      input.addEventListener('input', () => {
        if (status) { status.textContent = ''; }
        save();
      });
      input.addEventListener('blur', save);
    });
  }

  /* --------------------------------------------------------- dispatch day */

  /**
   * Names the day an order placed now would leave the warehouse. We dispatch
   * the next business day, so there's no cutoff clock to run — just an honest
   * statement of which day it is, which is worth more than a ticking timer.
   */
  function initDispatch() {
    const els = $$('[data-dispatch]');
    if (!els.length) return;

    const now = new Date();
    const next = new Date(now);
    do {
      next.setDate(next.getDate() + 1);
    } while (next.getDay() === 0 || next.getDay() === 6);

    const isTomorrow = (next - now) / 86400000 < 1.5 && next.getDate() === now.getDate() + 1;
    const dayName = next.toLocaleDateString(undefined, { weekday: 'long' });

    els.forEach((el) => {
      el.textContent = isTomorrow
        ? 'Order today and it leaves the warehouse tomorrow.'
        : `Order today and it leaves the warehouse ${dayName}.`;
    });
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    initProductPage();
    initHeroTiers();
    initCart();
    initMobileNav();
    initSearch();
    initPLP();
    initQuickOrder();
    initAccount();
    initCartAttributes();
    initDispatch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
